import { useEffect, useState } from 'react';
import type { CreditLedgerEntry } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';

/**
 * Prices and credit costs live in the entitlement snapshot. Until it arrives,
 * say so — a blank number next to "credits" is worse than nothing on the one
 * screen whose whole job is telling people what things cost.
 */
function Pending({ label }: { label: string }) {
  return (
    <div className="card row">
      <span className="spinner" />
      <span className="small muted">{label}</span>
    </div>
  );
}

/** AI-credit shop + auditable wallet history. */
export function CreditShopScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [ledger, setLedger] = useState<CreditLedgerEntry[]>([]);
  const [ledgerState, setLedgerState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const snap = app.entitlement;
  const packs = snap?.config.creditPacks;
  const costs = snap?.config.creditCosts;
  const purchasing = busy !== null;

  /**
   * A failed fetch used to be swallowed, and the history only rendered when
   * non-empty — so "we couldn't reach the server" looked exactly like "you
   * have never spent a credit". On a wallet that has to be auditable, those
   * two must never be confusable.
   */
  const loadLedger = async () => {
    setLedgerState('loading');
    try {
      setLedger(await app.backend.listLedger());
      setLedgerState('ready');
    } catch {
      setLedgerState('error');
    }
  };

  useEffect(() => {
    loadLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap?.creditBalance]);

  const buy = async (packId: string) => {
    setBusy(packId);
    setError('');
    try {
      await app.purchase(packId);
      await loadLedger();
    } catch (e) {
      const raw = String(e);
      setError(
        raw.includes('purchase-in-app-only') || raw.includes('play-billing-pending')
          ? 'Credit packs are purchased through Google Play in the Astrally Android app. Your credits then apply to this account here too.'
          : raw.slice(0, 200),
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader
        title={t('credits.title')}
        subtitle={t('credits.body')}
        back
        // An unloaded wallet is not a wallet with zero in it.
        right={<span className="badge badge--C">{snap ? snap.creditBalance : '…'}</span>}
      />

      {/* The one fact this screen must never let a Pro pitch obscure. */}
      <div className="small muted" style={{ marginBottom: 10, lineHeight: 1.5 }}>
        {t('credits.bothTiers')}
      </div>

      {purchasing && (
        <div className="banner banner--info small row" style={{ marginBottom: 10 }}>
          <span className="spinner" />
          <span>Completing your purchase…</span>
        </div>
      )}

      {error && <div className="banner banner--danger small" style={{ marginBottom: 10 }}>{error}</div>}

      <Section title={t('credits.buy')}>
        {packs ? (
          <div className="col">
            {packs.map((pack) => (
              <div key={pack.id} className="card spread">
                <div>
                  <div className="h3">{pack.name}</div>
                  <div className="tiny muted">{pack.credits} credits · never expire</div>
                </div>
                <button
                  // The pack you are not buying goes muted rather than plainly
                  // dead — the banner above says why every button is waiting.
                  className={`btn btn--primary btn--small${
                    purchasing && busy !== pack.id ? ' btn--incomplete' : ''
                  }`}
                  disabled={purchasing}
                  onClick={() => buy(pack.id)}
                >
                  {busy === pack.id ? <span className="spinner" /> : pack.price.inr}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <Pending label={t('unlock.pricePending')} />
        )}
      </Section>

      <Section title={t('credits.costs')} term="ai-credits">
        {costs ? (
          <div className="card col" style={{ gap: 6 }}>
            <div className="spread small">
              <span>{t('ai.deeper')} answer</span>
              <span className="gold">{costs.deeperAnswer} credits</span>
            </div>
            <div className="spread small">
              <span>Deep stored report</span>
              <span className="gold">{costs.deeperReport} credits</span>
            </div>
            <div className="spread small">
              <span>{t('ai.master')}</span>
              <span className="gold">{costs.masterReading} credits</span>
            </div>
            <div className="spread small">
              <span>Regenerate a stored report</span>
              <span className="gold">{costs.regeneration} credits</span>
            </div>
            <div className="tiny faint">
              Costs are always shown before a premium action. Failed generations refund
              automatically.
            </div>
          </div>
        ) : (
          <Pending label={t('unlock.pricePending')} />
        )}
      </Section>

      {/* Always rendered, in all three states — an absent section reads as
          "you have no history", which may simply be untrue. */}
      <Section title={t('credits.ledger')}>
        {ledgerState === 'loading' && <Pending label={t('common.loading')} />}
        {ledgerState === 'error' && (
          <div className="card col">
            <div className="small">Your credit history could not be loaded.</div>
            <button className="btn btn--ghost btn--small" onClick={() => loadLedger()}>
              {t('common.retry')}
            </button>
          </div>
        )}
        {ledgerState === 'ready' &&
          (ledger.length === 0 ? (
            <div className="card small muted">
              Nothing yet — every credit you buy or spend is listed here.
            </div>
          ) : (
            <div className="card col" style={{ gap: 6 }}>
              {ledger.slice(0, 25).map((e) => (
                <div key={e.id} className="spread small">
                  <span className="muted">
                    {e.createdAtISO.slice(0, 10)} · {e.source.replace(/_/g, ' ')}
                  </span>
                  <span className={e.type === 'credit' ? 'gold' : 'danger'}>
                    {e.type === 'credit' ? '+' : '−'}{e.amount} → {e.balanceAfter}
                  </span>
                </div>
              ))}
            </div>
          ))}
      </Section>
    </div>
  );
}
